<script setup lang="ts">
    import { computed, onMounted, onUnmounted, ref } from 'vue';

    const phrases = [
        'cottage bakery',
        'salsa lab',
        'farmstand',
        'porch pickup',
        'home bakery',
        'candy kitchen',
        'cookie business',
        'micro bakery',
        'hot sauce',
        'customers!',
    ] as const;

    const maxChars = Math.max(...phrases.map((phrase) => phrase.length));

    const typed = ref<string>(phrases[0]);

    const basePurple = { r: 114, g: 87, b: 209 }; // #7257d1
    const white = { r: 255, g: 255, b: 255 };

    const speedMultiplier = 1.5625;

    const rgbToCss = (rgb: { r: number; g: number; b: number }) => `rgb(${rgb.r} ${rgb.g} ${rgb.b})`;
    const mix = (a: number, b: number, t: number) => Math.round(a + (b - a) * t);

    const charColors = computed(() => {
        const text = typed.value;
        const length = Math.max(text.length, 1);
        const maxLighten = 0.468;

        return Array.from(text).map((ch, index) => {
            const t = length <= 1 ? 0 : (index / (length - 1)) * maxLighten;
            const rgb = {
                r: mix(basePurple.r, white.r, t),
                g: mix(basePurple.g, white.g, t),
                b: mix(basePurple.b, white.b, t),
            };

            return { ch, color: rgbToCss(rgb) };
        });
    });

    let phraseIndex = 0;
    let charIndex = phrases[0].length;
    let mode: 'typing' | 'deleting' = 'typing';
    let timer: number | null = null;

    const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const schedule = (ms: number, fn: () => void) => {
        timer = window.setTimeout(fn, ms);
    };

    const step = () => {
        const phrase = phrases[phraseIndex];

        if (mode === 'typing') {
            if (charIndex < phrase.length) {
                charIndex += 1;
                typed.value = phrase.slice(0, charIndex);
                schedule(55 * speedMultiplier, step);
                return;
            }

            schedule(1350 * speedMultiplier, () => {
                mode = 'deleting';
                schedule(35 * speedMultiplier, step);
            });
            return;
        }

        if (charIndex > 0) {
            charIndex -= 1;
            typed.value = phrase.slice(0, charIndex);
            schedule(35 * speedMultiplier, step);
            return;
        }

        phraseIndex = (phraseIndex + 1) % phrases.length;
        mode = 'typing';
        charIndex = 0;
        schedule(270 * speedMultiplier, step);
    };

    onMounted(() => {
        if (prefersReducedMotion) {
            typed.value = phrases[0];
            return;
        }

        typed.value = phrases[0];
        phraseIndex = 0;
        charIndex = 0;
        mode = 'typing';
        schedule(525 * speedMultiplier, step);
    });

    onUnmounted(() => {
        if (timer) {
            window.clearTimeout(timer);
            timer = null;
        }
    });
</script>

<template>
    <span class="typing" :style="{ '--typing-width': `${maxChars}ch` } as Record<string, string>" aria-label="cottage bakery">
        <span class="typing__text">
            <span v-for="(c, i) in charColors" :key="`${c.ch}-${i}`" class="typing__char" :style="{ color: c.color }">
                {{ c.ch === ' ' ? '\u00A0' : c.ch }}
            </span>
        </span>
        <span class="typing__cursor" aria-hidden="true" />
    </span>
</template>

<style scoped lang="scss">
    .typing {
        display: inline-flex;
        align-items: baseline;
        gap: 2px;
        width: var(--typing-width);
        white-space: nowrap;
        line-height: 1.2;

        &__text {
            display: inline-block;
            font-weight: 700;
        }

        &__char {
            display: inline-block;
        }

        &__cursor {
            display: inline-block;
            width: 0.275ch;
            height: 1em;
            background: #7257d1;
            animation: blink 1.1s step-end infinite;
        }
    }

    @keyframes blink {
        0%,
        50% {
            opacity: 1;
        }
        51%,
        100% {
            opacity: 0;
        }
    }

    @media (max-width: 600px) {
        .typing {
            justify-content: center;
        }
    }
</style>
